<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { $t } from '@teachino/shared-i18n';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import TTooltip from '../TTooltip.vue';
import type {
  ProFeatureBadgePlacement,
  ProFeatureBadgeProps,
} from './pro-feature-badge';

const PRO_FEATURE_BADGE_ICON_SIZE = 10;

const props = withDefaults(defineProps<ProFeatureBadgeProps>(), {
  show: true,
  placement: 'overlay',
});

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();

const badgeAriaLabel = computed(
  () => props.ariaLabel ?? $t('pro_feature_badge.tooltip'),
);

const badgeTooltip = computed(() => badgeAriaLabel.value);

const wrapperClass = 'relative inline-flex';

const badgePositionClassByPlacement: Record<ProFeatureBadgePlacement, string> =
  {
    overlay: '-top-1 -right-1',
    corner: 'top-0 right-0 translate-x-3/4 -translate-y-3/4',
  };

const badgePositionClass = computed(
  () => badgePositionClassByPlacement[props.placement],
);
</script>

<template>
  <div :class="wrapperClass" v-bind="attrs">
    <slot />
    <div v-if="show" class="absolute z-10" :class="badgePositionClass">
      <TTooltip :content="badgeTooltip" placement="top">
        <span
          class="border-primary-100 bg-primary-50 text-primary-600 flex size-4 cursor-default items-center justify-center rounded-full border shadow-sm"
          role="img"
          :aria-label="badgeAriaLabel"
        >
          <HugeIcon icon="crown" :size="PRO_FEATURE_BADGE_ICON_SIZE" />
        </span>
      </TTooltip>
    </div>
  </div>
</template>
